import { Container } from "aurelia-dependency-injection";
import { defineComponent } from "vue";

import { PatientService } from "../../../../../../services/patient.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { PatientRelations } from "../../../../../../models/pal-health-history/patient-relations";
import { RelationStatus } from "../../../../../../enums/RelationStatusEnum";
import { PAGE_FAIL_RESPONSE } from "../../../../../../resources-vue/vue-elements/Pagination/Pagination";
import { IPageCallbackResult } from "../../../../../../resources-vue/vue-interfaces/i-page-container";
import { IFamilyHealthHistory } from "../../../../../../resources-vue/vue-interfaces/i-health";
import { ApplicationEnum } from "../../../../../../enums/ApplicationEnum";
import { FamilyHealthHistoryModel } from "../../../../../../models/pal-health-history/family-health-history-model";
import { PatientRelationship } from "../../../../../../enums/PatientRelationshipsEnum";

import FamilyHistoryForm from "../FamilyHealthHistoryForm/FamilyHealthHistoryForm.vue";
import Pagination from "../../../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import ErrorMessageHandler from "../../../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import FamilyHistoryRow from "../FamilyHealthHistoryRow/FamilyHistoryRow.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";

export default defineComponent({
    components: {
        FamilyHistoryForm,
        Pagination,
        ErrorMessageHandler,
        FamilyHistoryRow,
        FormSection
    },

    props: {
        patientId: {
            type: String,
            default: ""
        },
        accountId: {
            type: String,
            default: ""
        },
        application: {
            type: Number
        }
    },
    data() {
        return {
            _patientsService: null as PatientService,
            _toastrService: null as ToastrService,
            isAddRequested: false as boolean,
            isLoading: false as boolean,
            isError: false as boolean,
            isSaving: false as boolean,
            familyPaginationInstance: null,
            columns: 5 as number,
            page: null,
            familyHealthHistory: null as IFamilyHealthHistory,
            patientRelationshipEnum: Array.from(PatientRelationship.enumWithDescriptions.values()),
            relationStatusEnum: Array.from(RelationStatus.enumWithDescriptions.values()),
            currentEditRowId: "" as string,
            pageSize: 10
        };
    },

    computed: {
        hasData() {
            return this.page?.ItemCount > 0;
        },

        loadingCount() {
            return this.page?.Items?.length > 0 ? this.page.Items.length : 3;
        }
    },

    created() {
        this._patientsService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this.familyPaginationInstance = this.$refs.familyPaginationInstance;
    },

    mounted() {
        this.familyPaginationInstance = this.$refs.familyPaginationInstance;
        this.reset();
    },

    methods: {
        openAddFamilyHistory() {
            this.familyHealthHistory = new FamilyHealthHistoryModel();
            this.familyHealthHistory.PatientRelations.push(new PatientRelations());
            this.isAddRequested = true;
        },

        async saveFamilyHistory(isSaveAndOpen: boolean = false) {
            const isValidForm = await (this.$refs.familyHistoryForm as any).v$.$validate();
            if (isValidForm) {
                const payload = {
                    Application: ApplicationEnum.AxxessPalliative,
                    AccountId: this.accountId,
                    PalliativePatientFamilyHealthHistory: {
                        ...this.familyHealthHistory
                    }
                };
                try {
                    this.isSaving = true;
                    await this._patientsService.createFamilyHealthHistory(this.patientId, payload);
                    this.cancelAddFamilyHistory();
                    this.reset();
                    if (isSaveAndOpen) {
                        // add settime out to remove race condition
                        setTimeout(() => this.openAddFamilyHistory(), 0);
                    }
                    this._toastrService.success({
                        title: "Family Health History Created",
                        message: "Family Health History has been successfully created."
                    });
                } catch (err) {
                    console.error(err);
                    this._toastrService.error({
                        title: "Error - Family Health History",
                        message: "There was an error creating Family Health History. Please try again."
                    });
                } finally {
                    this.isSaving = false;
                }
            } else {
                this._toastrService.error({
                    title: "Error - Family Health History",
                    message: "Please fill required fields"
                });
            }
        },

        cancelAddFamilyHistory() {
            this.isAddRequested = false;
        },

        reset() {
            this.familyPaginationInstance.reset();
        },

        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            try {
                this.isLoading = true;
                let query = {
                    accountId: this.accountId,
                    page: pageNumber,
                    pageLength: pageSize,
                    application: ApplicationEnum.AxxessPalliative
                };
                let data: any = await this._patientsService.getFamilyHealthHistory(this.patientId, query);
                this.page = data.data;
                this.isLoading = false;
                return {
                    totalPageCount: data.data.PageCount,
                    success: data.data.ItemCount > 0,
                    totalCount: data.data.ItemCount
                };
            } catch (e) {
                console.error(e);
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            }
        },
        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        }
    }
});
